import { getCookieName } from '@creditornot/cwc-cookie-sdk';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import { useLocation } from 'react-router';
import { useCookie } from 'react-use';
import styled from 'styled-components';

import { patchInboundMerchant } from 'apis/merchant-onboarding-service/api';
import { useTelemetryViewEvent } from 'common/hooks';
import { ssmoToolsAndOptionsView, ssmoToolsAndOptionsInteractionClickNext } from 'core/telemetry/avo/Avo';
import { DEVICE_PREFERENCES_LOCAL_STORAGE_ID, SsmoStepsIdentifiers } from 'modules/onboarding-page/constants';
import { InboundMerchant, SsmoCountryConfig } from 'modules/onboarding-page/types';

import { validateStepParam } from './helpers';
import { useRegisterTelemetryEvent } from '../../../../core/telemetry/hooks/useRegisterTelemetryEvent';
import { useCookieConsents } from '../../../cookie-consents/CookieConsentsProvider';
import { CountryConfig } from '../../../country/types';
import DevicePreferencesForm from '../forms/ServicePricingForm/DevicePreferencesForm';
import { DevicePreferences, DevicePreferencesSchema } from '../validation';

type DeviceSelectionProps = {
  inboundMerchant: InboundMerchant;
  ssmoCountryConfig: SsmoCountryConfig;
  goToNextStep: () => void;
  setUpdateIsInProgress: (inProgress: boolean) => void;
  countryConfig: CountryConfig;
};

const Wrapper = styled.div``;

const ContentContainer = styled.div`
  margin-block-end: 32px;
`;

const DeviceSelectionStep = ({
  inboundMerchant,
  ssmoCountryConfig,
  goToNextStep,
  setUpdateIsInProgress,
  countryConfig,
}: DeviceSelectionProps) => {
  const { search } = useLocation();

  useTelemetryViewEvent('ssmo_tools_and_options_view', {}, durationMs => ssmoToolsAndOptionsView({ durationMs }));

  const searchParams = new URLSearchParams(search);

  const register = useRegisterTelemetryEvent();
  const {
    consents: { analytics: analyticsConsent, marketing: marketingConsent },
  } = useCookieConsents();
  const [woltUid] = useCookie(getCookieName('wolt-uid'));
  const { country } = inboundMerchant;

  const handleDevicePreferencesSubmit = async (
    values: DevicePreferences,
  ): Promise<{
    [key: string]: string | Error;
  } | void> => {
    try {
      setUpdateIsInProgress(true);

      const stepParam = validateStepParam(searchParams.get('step'));

      const validFormValues = DevicePreferencesSchema.validateSync(values);

      register({
        event: 'device_selection_submitted',
        marketingConsent,
        analyticsConsent,
        properties: {
          __woltUid: woltUid,
          country,
        },
      });

      ssmoToolsAndOptionsInteractionClickNext();
      localStorage.removeItem(DEVICE_PREFERENCES_LOCAL_STORAGE_ID);

      await patchInboundMerchant({ ...validFormValues, ssmoStepCompletionId: stepParam }, inboundMerchant.merchantId);
      goToNextStep();
    } catch (submitError) {
      return { [FORM_ERROR]: submitError as Error };
    } finally {
      setUpdateIsInProgress(false);
    }
  };

  return (
    <Wrapper>
      <ContentContainer>
        <DevicePreferencesForm
          deviceConfig={ssmoCountryConfig.devicesConfig}
          devicePreferences={{
            needsPrinter: inboundMerchant!.needsPrinter || false,
            needsMobileDataPackage: inboundMerchant!.needsMobileDataPackage || false,
          }}
          handleSubmit={handleDevicePreferencesSubmit}
          countryConfig={countryConfig}
          formId={SsmoStepsIdentifiers.DeviceSelection}
        />
      </ContentContainer>
    </Wrapper>
  );
};

export default DeviceSelectionStep;
